import {
  CompositionGroup,
  compositionGroupApi,
} from '@texas/api/endpoints/compositionGroup/compositionGroupApi';
import { compositionGroupMatrixApi } from '@texas/api/endpoints/compositionGroup/compositionGroupMatrixApi';
import {
  inquiryApi,
  InquiryWithContext,
} from '@texas/api/endpoints/inquiryApi';
import {
  offerApi,
  OfferedOffer,
  OfferGroupSummary,
} from '@texas/api/endpoints/offerApi';
import {
  ArticleBranch,
  variantsApi,
  VariantBranchSupplier,
} from '@texas/api/endpoints/variantsApi';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useArticleContext } from '@texas/components/article/articleContext/useArticleContext';
import { useActiveSession } from '@texas/hooks/useSession';
import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export interface PdOverviewStage {
  count: number;
  description?: string;
  label: string;
  loading: boolean;
  route: string;
  navobj?: Record<string, unknown>;
  tone?: 'attention';
}

export interface PdBranchOverviewRow {
  branch: ArticleBranch;
  inquiries: { accepted: number; open: number; sent: number };
  latestVersion: number | null;
  offers: {
    draft: number;
    offeredPriceCount: number;
    sent: number;
    totalPriceCount: number;
  };
  options: { ready: number; total: number };
  suppliers: string[];
  versions: number;
}

const EMPTY_BRANCH_IDS: number[] = [];

export function usePdOverview(isMatrix: boolean, variantId: number) {
  const { t } = useTranslation();
  const { currentUser } = useActiveSession();
  const articleContext = useArticleContext();
  const selectedBranchIds =
    articleContext.variantId === variantId
      ? articleContext.branchIds
      : EMPTY_BRANCH_IDS;
  const {
    data: branches,
    error: branchesError,
    loading: branchesLoading,
    refetch: refetchBranches,
  } = useApiResource(variantsApi.getArticleBranches);
  const {
    data: options,
    error: optionsError,
    loading: optionsLoading,
    refetch: refetchOptions,
  } = useApiResource(compositionGroupApi.optionsOverview);
  const {
    data: matrixOptions,
    error: matrixOptionsError,
    loading: matrixOptionsLoading,
    refetch: refetchMatrixOptions,
  } = useApiResource(compositionGroupMatrixApi.getOptions);
  const {
    data: versions,
    error: versionsError,
    loading: versionsLoading,
    refetch: refetchVersions,
  } = useApiResource(compositionGroupApi.versionsOverview);
  const {
    data: suppliers,
    error: suppliersError,
    loading: suppliersLoading,
    refetch: refetchSuppliers,
  } = useApiResource(variantsApi.getVariantBranchSuppliers);
  const {
    data: inquiries,
    error: inquiriesError,
    loading: inquiriesLoading,
    refetch: refetchInquiries,
  } = useApiResource(inquiryApi.getByVariant);
  const {
    data: offerGroups,
    error: offerGroupsError,
    loading: offerGroupsLoading,
    refetch: refetchOfferGroups,
  } = useApiResource(offerApi.getByVariant);
  const {
    data: offeredOffers,
    error: offeredOffersError,
    loading: offeredOffersLoading,
    refetch: refetchOfferedOffers,
  } = useApiResource(offerApi.getOfferedOffersByVariant);

  useEffect(() => {
    refetchBranches(variantId);
    if (isMatrix) refetchMatrixOptions(variantId);
    else refetchOptions({ variantId });
    refetchSuppliers(variantId, true);
    if (currentUser.betaFeatures) {
      refetchOfferGroups(variantId, true);
      refetchOfferedOffers(variantId);
    }
  }, [
    currentUser.betaFeatures,
    isMatrix,
    refetchBranches,
    refetchMatrixOptions,
    refetchOfferGroups,
    refetchOfferedOffers,
    refetchOptions,
    refetchSuppliers,
    variantId,
  ]);

  useEffect(() => {
    const branchIds =
      selectedBranchIds.length > 0 ? selectedBranchIds : undefined;
    refetchVersions({ variantId, branchIds });
    if (currentUser.betaFeatures) {
      refetchInquiries({ variantId, branchIds, showArchived: true });
    }
  }, [
    currentUser.betaFeatures,
    refetchInquiries,
    refetchVersions,
    selectedBranchIds,
    variantId,
  ]);

  const overview = useMemo(() => {
    const isBranchVisible = (branchId: number) =>
      selectedBranchIds.length === 0 || selectedBranchIds.includes(branchId);
    const visibleBranches = (branches ?? []).filter((branch) =>
      isBranchVisible(branch.branchId),
    );
    const activeOptions = ((isMatrix ? matrixOptions : options) ?? []).filter(
      (option) => !option.archived,
    );
    const allVersions = versions ?? [];
    const activeVersions = allVersions.filter((version) => !version.archived);
    const archivedVersions = allVersions.filter((version) => version.archived);
    const visibleSuppliers = (suppliers?.suppliers ?? []).filter(
      (supplier) => !supplier.disabled && isBranchVisible(supplier.branchId),
    );
    const activeInquiries = (inquiries?.inquiries ?? []).filter(
      (inquiry) => !inquiry.archived,
    );
    const activeMasterInquiries = activeInquiries.filter(
      (inquiry) => inquiry.inquiryControlledById === null,
    );
    const activeOfferGroups = (offerGroups ?? []).filter(
      (group) =>
        !group.archived &&
        [...group.coveredBranches, ...group.missingBranches].some((branch) =>
          isBranchVisible(branch.branchId),
        ),
    );
    const activeOfferedOffers = (offeredOffers ?? []).filter(
      (offer) =>
        !offer.cancelledAt &&
        offer.branches.some((branch) => isBranchVisible(branch.branchId)),
    );
    const inquiryCounts = getInquiryCounts(activeMasterInquiries);
    const activeVersionCount = logicalVersionCount(activeVersions);
    const archivedVersionCount = logicalVersionCount(archivedVersions);
    const readyOptionCount = activeOptions.filter(
      (option) => option.isReady,
    ).length;
    const branchRows = visibleBranches.map((branch) =>
      createBranchRow({
        activeVersions,
        branch,
        inquiries: activeInquiries,
        offerGroups: activeOfferGroups,
        offeredOffers: activeOfferedOffers,
        options: activeOptions,
        suppliers: visibleSuppliers,
      }),
    );
    const stages: PdOverviewStage[] = [
      {
        count: activeOptions.length,
        description: t('productDevelopmentOverview.readyAndInProgress', {
          ready: readyOptionCount,
          inProgress: activeOptions.length - readyOptionCount,
        }),
        label: t('navigation.options'),
        loading: isMatrix ? matrixOptionsLoading : optionsLoading,
        route: 'variant.options',
      },
      {
        count: activeVersionCount,
        description: t('productDevelopmentOverview.activeAndArchived', {
          active: activeVersionCount,
          archived: archivedVersionCount,
        }),
        label: t('navigation.versions'),
        loading: versionsLoading,
        route: 'variant.versions',
      },
      {
        count: uniqueSupplierCount(visibleSuppliers),
        label: t('navigation.suppliers'),
        loading: suppliersLoading,
        route: 'variant.suppliers',
      },
      ...(currentUser.betaFeatures
        ? [
            {
              count: activeMasterInquiries.length,
              description: t('productDevelopmentOverview.draftAndAccepted', {
                draft: inquiryCounts.open,
                accepted: inquiryCounts.accepted,
              }),
              label: t('navigation.inquiry'),
              loading: inquiriesLoading,
              route: 'variant.inquiry',
              navobj: { status: 'all' },
              tone: inquiryCounts.open > 0 ? ('attention' as const) : undefined,
            },
            {
              count: activeOfferGroups.length + activeOfferedOffers.length,
              description: t('productDevelopmentOverview.draftAndSent', {
                draft: activeOfferGroups.length,
                sent: activeOfferedOffers.length,
              }),
              label: t('navigation.offers'),
              loading: offerGroupsLoading || offeredOffersLoading,
              route: 'variant.offers',
            },
          ]
        : []),
    ];

    return {
      branchRows,
      hasActivityAcrossEveryStage: stages.every((stage) => stage.count > 0),
      stages,
    };
  }, [
    branches,
    currentUser.betaFeatures,
    inquiries,
    inquiriesLoading,
    isMatrix,
    matrixOptions,
    matrixOptionsLoading,
    offerGroups,
    offerGroupsLoading,
    offeredOffers,
    offeredOffersLoading,
    options,
    optionsLoading,
    selectedBranchIds,
    suppliers,
    suppliersLoading,
    t,
    versions,
    versionsLoading,
  ]);

  const error =
    [
      branchesError,
      isMatrix ? matrixOptionsError : optionsError,
      versionsError,
      suppliersError,
      ...(currentUser.betaFeatures
        ? [inquiriesError, offerGroupsError, offeredOffersError]
        : []),
    ].find((resourceError) => resourceError !== null) ?? null;
  const isLoading = [
    branchesLoading,
    isMatrix ? matrixOptionsLoading : optionsLoading,
    versionsLoading,
    suppliersLoading,
    ...(currentUser.betaFeatures
      ? [inquiriesLoading, offerGroupsLoading, offeredOffersLoading]
      : []),
  ].some(Boolean);
  const scopeLabel =
    selectedBranchIds.length === 0
      ? t('filter.allBranches')
      : selectedBranchIds.length === 1 &&
        selectedBranchIds[0] === currentUser.branchId
      ? t('navigation.myBranch', {
          identifier:
            overview.branchRows[0]?.branch.identifier ??
            currentUser.branchIdentifier,
        })
      : selectedBranchIds.length === 1
      ? overview.branchRows[0]?.branch.identifier ?? t('general.branch')
      : t('general.branchesCount', { count: selectedBranchIds.length });

  return {
    branchesLoading,
    currentBranchId: currentUser.branchId,
    error,
    isLoading,
    overview,
    scopeLabel,
  };
}

function createBranchRow({
  activeVersions,
  branch,
  inquiries,
  offerGroups,
  offeredOffers,
  options,
  suppliers,
}: {
  activeVersions: CompositionGroup[];
  branch: ArticleBranch;
  inquiries: InquiryWithContext[];
  offerGroups: OfferGroupSummary[];
  offeredOffers: OfferedOffer[];
  options: CompositionGroup[];
  suppliers: VariantBranchSupplier[];
}): PdBranchOverviewRow {
  const branchVersions = activeVersions.filter(
    (version) => version.branchId === branch.branchId,
  );
  const branchSuppliers = Array.from(
    new Map(
      suppliers
        .filter((supplier) => supplier.branchId === branch.branchId)
        .map((supplier) => [supplier.supplierId, supplier.supplierName]),
    ).values(),
  );
  const branchInquiries = inquiries.filter(
    (inquiry) => inquiry.branchIdentifier === branch.identifier,
  );
  const branchOfferGroups = offerGroups.filter((group) =>
    [...group.coveredBranches, ...group.missingBranches].some(
      (offerBranch) => offerBranch.branchId === branch.branchId,
    ),
  );
  const branchOfferedOffers = offeredOffers.filter((offer) =>
    offer.branches.some(
      (offerBranch) => offerBranch.branchId === branch.branchId,
    ),
  );
  const branchCurrencies = branchOfferGroups.flatMap((group) =>
    group.branchCurrencies.filter(
      (currency) => currency.branchId === branch.branchId,
    ),
  );

  return {
    branch,
    inquiries: getInquiryCounts(branchInquiries),
    latestVersion:
      branchVersions.length === 0
        ? null
        : Math.max(...branchVersions.map((version) => version.version)),
    offers: {
      draft: branchOfferGroups.length,
      offeredPriceCount: branchCurrencies.reduce(
        (total, currency) => total + currency.offeredPriceCount,
        0,
      ),
      sent: branchOfferedOffers.length,
      totalPriceCount: branchCurrencies.reduce(
        (total, currency) => total + currency.totalPriceCount,
        0,
      ),
    },
    options: {
      ready: options.filter((option) => option.isReady).length,
      total: options.length,
    },
    suppliers: branchSuppliers,
    versions: logicalVersionCount(branchVersions),
  };
}

function getInquiryCounts(inquiries: InquiryWithContext[]) {
  return inquiries
    .filter((inquiry) => inquiry.inquiryControlledById === null)
    .reduce(
      (counts, inquiry) => {
        if (inquiry.acceptedDate) counts.accepted += 1;
        else if (inquiry.sent) counts.sent += 1;
        else counts.open += 1;
        return counts;
      },
      { accepted: 0, open: 0, sent: 0 },
    );
}

function logicalVersionCount(versions: CompositionGroup[]) {
  return new Set(
    versions.map((version) => version.controlledById ?? version.id),
  ).size;
}

function uniqueSupplierCount(suppliers: VariantBranchSupplier[]) {
  return new Set(suppliers.map((supplier) => supplier.supplierId)).size;
}
